import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../contexts/UserContext';
import axios from 'axios';
import { FileText, Eye, Filter, ArrowUp, ArrowDown, Search, PlusCircle, ChevronDown, ChevronUp, X } from 'lucide-react';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const RequestList = ({ onNavigate, initialFilterStatus = 'all' }) => {
  const { user } = useContext(UserContext);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'status', direction: 'asc' });
  const [filterStatus, setFilterStatus] = useState(initialFilterStatus);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchWorkPermits = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/workpermit/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      setRequests(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching work permits:', error);
      setRequests([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkPermits();
  }, []);

  // Reset to first page when items per page changes or when filtered results change
  useEffect(() => {
    const sortedData = getSortedRequests();
    const calculatedTotalPages = Math.ceil(sortedData.length / itemsPerPage) || 1;
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(1);
    }
  }, [itemsPerPage, requests.length, filterStatus, searchTerm, currentPage]);

  const updateWorkPermitStatus = async (
    requestId,
    currentStatus,
    editedStartDate,
    editedEndDate,
    teamId = null
  ) => {
    try {
      let newStatus = '';
      if (currentStatus === 'planned_approved') {
        newStatus = 'signed';
      } else if (currentStatus === 'approved') {
        newStatus = 'closed';
      } else {
        alert('Status update not allowed for this status.');
        return;
      }

      const payload = {
        status: newStatus,
        planned_date_start: editedStartDate,
        planned_date_end: editedEndDate,
      };

      if (teamId) {
        payload.team = teamId;
      }

      const response = await axios.patch(
        `${API_BASE_URL}/workpermit/${requestId}/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        setShowDetails(false);
        await fetchWorkPermits(); // Refresh data after update
      }
    } catch (error) {
      console.error('Error updating work permit:', error);
      alert('Failed to update work permit.');
    }
  };

  const getStatusInfo = (status) => {
    const statusInfo = {
      planned_approved: { color: 'bg-amber-100 text-amber-800', displayName: 'Pending' },
      signed: { color: 'bg-sky-100 text-sky-800', displayName: 'Signed' },
      approved: { color: 'bg-emerald-100 text-emerald-800', displayName: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', displayName: 'Rejected' },
      closed: { color: 'bg-emerald-100 text-emerald-800', displayName: 'Completed' },
    };
    return statusInfo[status] || { color: 'bg-slate-100 text-slate-800', displayName: 'Unknown Status' };
  };

  const formatDateTime = (dateTime) => {
    return new Date(dateTime).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const sortRequests = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const renderSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" strokeWidth={1.5} />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" strokeWidth={1.5} />
    );
  };

  const getSortedRequests = () => {
    if (!requests) return [];

    let filteredRequests = [...requests];

    if (filterStatus !== 'all') {
      filteredRequests = filteredRequests.filter((request) => request.status === filterStatus);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filteredRequests = filteredRequests.filter((request) => {
        const matchesUser = request.team_data?.users?.some((user) =>
          `${user.first_name} ${user.last_name}`.toLowerCase().includes(term)
        );
        return (
          request.work_permit_number?.toLowerCase().includes(term) ||
          request.order_number?.toLowerCase().includes(term) ||
          request.turbine_data?.turbine_number?.toLowerCase().includes(term) ||
          request.turbine_data?.location?.toLowerCase().includes(term) ||
          request.company_name?.toLowerCase().includes(term) ||
          request.work_scope_data?.name?.toLowerCase().includes(term) ||
          matchesUser
        );
      });
    }

    const statusOrder = {
      planned_approved: 0,
      signed: 1,
      approved: 2,
      closed: 3,
      rejected: 4,
    };

    filteredRequests.sort((a, b) => {
      if (sortConfig.key === 'status') {
        const statusA = statusOrder[a.status] ?? 999;
        const statusB = statusOrder[b.status] ?? 999;
        return sortConfig.direction === 'asc' ? statusA - statusB : statusB - statusA;
      } else if (sortConfig.key === 'work_permit_number') {
        const valA = a.work_permit_number ?? '';
        const valB = b.work_permit_number ?? '';
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      } else if (sortConfig.key === 'order_number') {
        const valA = a.order_number ?? '';
        const valB = b.order_number ?? '';
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      } else if (sortConfig.key === 'turbine_data.turbine_number') {
        const valA = a.turbine_data?.turbine_number ?? '';
        const valB = b.turbine_data?.turbine_number ?? '';
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      } else if (sortConfig.key === 'planned_date_start') {
        const valA = a.planned_date_start ? new Date(a.planned_date_start) : new Date(0);
        const valB = b.planned_date_start ? new Date(b.planned_date_start) : new Date(0);
        return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
      }
      const statusA = statusOrder[a.status] ?? 999;
      const statusB = statusOrder[b.status] ?? 999;
      return sortConfig.direction === 'asc' ? statusA - statusB : statusB - statusA;
    });

    return filteredRequests;
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetails(true);
  };

  const handleViewDocuments = (request) => {
    if (!request.attachments) {
      alert('No attachments available for this request.');
      return;
    }
    if (typeof request.attachments === 'string') {
      window.open(request.attachments, '_blank');
    } else if (Array.isArray(request.attachments) && request.attachments.length > 0) {
      window.open(request.attachments[0], '_blank');
    } else {
      alert('Invalid or empty attachments.');
    }
  };

  const RequestDetailsModal = () => {
    const [showTeamMembers, setShowTeamMembers] = useState(true);
    const [userList, setUserList] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editedStartDate, setEditedStartDate] = useState(selectedRequest?.planned_date_start || '');
    const [editedEndDate, setEditedEndDate] = useState(selectedRequest?.planned_date_end || '');
    const [editedTeam, setEditedTeam] = useState(selectedRequest?.team_data?.users || []);
    const [teamLeaderId, setTeamLeaderId] = useState(selectedRequest?.team_data?.team_leader || null);
    const [preparerId, setPreparerId] = useState(selectedRequest?.team_data?.person_responsible_for_preparation_of_work_place || null);
    const [teamIds, setTeamIds] = useState(editedTeam.map((member) => member.id));
    const [addMemberValue, setAddMemberValue] = useState('');

    useEffect(() => {
      const fetchUsers = async () => {
        try {
          if (isEditing && userList.length === 0) {
            const response = await fetch(`${API_BASE_URL}/users/minimal/`, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('access_token')}`,
              },
            });
            const data = await response.json();
            setUserList(data);
          }
        } catch (error) {
          console.error('Error fetching users:', error);
          alert('Failed to fetch user list.');
        }
      };

      fetchUsers();
    }, [isEditing, userList.length]);

    useEffect(() => {
      console.log('Updated team:', editedTeam);
    }, [editedTeam]);

    if (!showDetails || !selectedRequest) return null;

    const isSupervisor = selectedRequest.team_data?.team_leader === user?.id;

    const getWeekNumber = (date) => {
      const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
      const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
      return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay()) / 7);
    };

    const handleChangeLeader = (newLeaderId) => {
      const newLeader = userList.find((user) => user.id === newLeaderId);
      if (newLeader) {
        const updatedTeam = editedTeam.filter((member) => member.id !== teamLeaderId);
        if (!updatedTeam.some((member) => member.id === newLeaderId)) {
          updatedTeam.push(newLeader);
        }
        setTeamLeaderId(newLeaderId);
        setEditedTeam(updatedTeam);
        setTeamIds(updatedTeam.map((member) => member.id));
      }
    };

    const handlePreparerChange = (newPreparerId) => {
      if (newPreparerId === 0) {
        if (preparerId) {
          setEditedTeam(editedTeam.filter((member) => member.id !== preparerId));
          setTeamIds(teamIds.filter((id) => id !== preparerId));
        }
        setPreparerId(null);
        return;
      }

      if (preparerId && preparerId !== newPreparerId) {
        setEditedTeam(editedTeam.filter((member) => member.id !== preparerId));
        setTeamIds(teamIds.filter((id) => id !== preparerId));
      }

      if (!teamIds.includes(newPreparerId)) {
        const newPreparer = userList.find((user) => user.id === newPreparerId);
        if (newPreparer) {
          setEditedTeam([...editedTeam, newPreparer]);
          setTeamIds([...teamIds, newPreparerId]);
        }
      }

      setPreparerId(newPreparerId);
    };

    const handleAddMember = (userId) => {
      if (!teamIds.includes(userId)) {
        const newMember = userList.find((user) => user.id === userId);
        if (newMember) {
          setEditedTeam([...editedTeam, newMember]);
          setTeamIds([...teamIds, userId]);
          setAddMemberValue('');
        }
      }
    };

    const handleRemoveMember = (userId) => {
      if (userId === teamLeaderId) {
        alert('Cannot remove the supervisor. Change the supervisor first.');
        return;
      }

      const updatedTeam = editedTeam.filter((member) => member.id !== userId);
      const updatedTeamIds = teamIds.filter((id) => id !== userId);

      if (userId === preparerId) {
        setPreparerId(null);
      }

      setEditedTeam(updatedTeam);
      setTeamIds(updatedTeamIds);
    };

    const isTeamEdited = () => {
      const initialTeamIds = selectedRequest.team_data.users.map((user) => user.id).sort((a, b) => a - b);
      const currentTeamIds = [...teamIds].sort((a, b) => a - b);
      return JSON.stringify(initialTeamIds) !== JSON.stringify(currentTeamIds);
    };

    const handleSave = async () => {
      const oldTeamName = selectedRequest?.team_data?.name || 'UnnamedTeam';
      const newTeamName = `${oldTeamName}_edited`;

      let activePeriods = {};
      if (editedStartDate) {
        const startDate = new Date(editedStartDate);
        const year = startDate.getFullYear().toString();
        const weekNumber = getWeekNumber(startDate);
        activePeriods = {
          [year]: [weekNumber],
        };
      }

      const teamData = {
        name: newTeamName,
        team_leader: teamLeaderId,
        person_responsible_for_preparation_of_work_place: preparerId,
        team: teamIds,
        active_periods: activePeriods,
      };

      try {
        let newTeamId = null;

        if (isTeamEdited()) {
          const response = await fetch(`${API_BASE_URL}/teams/`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(teamData),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Failed to save team data: ${errorData.detail || 'Unknown error'}`);
          }

          const data = await response.json();
          newTeamId = data.id;
        }

        await updateWorkPermitStatus(
          selectedRequest.id,
          selectedRequest.status,
          editedStartDate,
          editedEndDate,
          newTeamId
        );

        alert('Data saved successfully!');
        setIsEditing(false);
      } catch (error) {
        console.error('Error saving data:', error);
        alert('Failed to save data.');
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-brand-50 to-white px-6 py-4 border-b border-brand-100 flex items-center justify-between flex-shrink-0">
            <h3 className="text-lg font-bold text-slate-900">
              Work Permit Details: {selectedRequest.work_permit_number}
            </h3>
            <button
              onClick={() => setShowDetails(false)}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-1">

            <div className="space-y-5">
              <div className="flex items-center space-x-2">
                <label className="text-xs font-bold text-slate-500 uppercase">Status:</label>
                {(() => {
                  const { color, displayName } = getStatusInfo(selectedRequest.status);
                  return (
                    <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${color}`}>
                      {displayName}
                    </span>
                  );
                })()}
              </div>

              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setShowTeamMembers(!showTeamMembers)}
                  className="w-full px-4 py-3 bg-slate-50 flex items-center justify-between hover:bg-slate-100 transition-colors duration-200"
                >
                  <span className="text-sm font-bold text-slate-900">
                    Team Members ({editedTeam.length})
                  </span>
                  {showTeamMembers ? <ChevronUp className="h-5 w-5 text-slate-500" /> : <ChevronDown className="h-5 w-5 text-slate-500" />}
                </button>

                {showTeamMembers && (
                  <div className="p-4 space-y-3">
                    <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-bold text-slate-500 uppercase">Supervisor:</span>
                        {isEditing ? (
                          <select
                            value={teamLeaderId}
                            onChange={(e) => handleChangeLeader(parseInt(e.target.value))}
                            className="text-sm border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                          >
                            {userList.map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.first_name} {user.last_name} ({user.username})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-sm font-medium text-slate-800">
                            {editedTeam.find((member) => member.id === teamLeaderId)?.first_name || ''}{' '}
                            {editedTeam.find((member) => member.id === teamLeaderId)?.last_name || ''}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-bold text-slate-500 uppercase">Preparer:</span>
                        {isEditing ? (
                          <select
                            value={preparerId || ''}
                            onChange={(e) => handlePreparerChange(parseInt(e.target.value))}
                            className="text-sm border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                          >
                            <option value="0">None</option>
                            {userList.map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.first_name} {user.last_name} ({user.username})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-sm font-medium text-slate-800">
                            {editedTeam.find((member) => member.id === preparerId)?.first_name || ''}{' '}
                            {editedTeam.find((member) => member.id === preparerId)?.last_name || 'None'}
                          </span>
                        )}
                      </div>
                      {isEditing && preparerId && (
                        <button
                          onClick={() => handlePreparerChange(0)}
                          className="text-red-500 hover:text-red-600 text-sm font-medium"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    {editedTeam
                      .filter((member) => member.id !== teamLeaderId && member.id !== preparerId)
                      .map((member) => (
                        <div key={member.id} className="flex justify-between items-center bg-slate-50 p-3 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-bold text-slate-500 uppercase">Member:</span>
                            <span className="text-sm font-medium text-slate-800">
                              {member.first_name} {member.last_name}
                            </span>
                          </div>
                          {isEditing && (
                            <button
                              onClick={() => handleRemoveMember(member.id)}
                              className="text-red-500 hover:text-red-600 text-sm font-medium"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}

                    {isEditing && (
                      <div className="flex items-center space-x-2">
                        <select
                          value={addMemberValue}
                          onChange={(e) => {
                            const userId = parseInt(e.target.value);
                            if (userId) {
                              handleAddMember(userId);
                            }
                          }}
                          className="text-sm border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        >
                          <option value="" disabled>
                            Add Member
                          </option>
                          {userList
                            .filter((user) => !editedTeam.some((member) => member.id === user.id))
                            .map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.first_name} {user.last_name} ({user.username})
                              </option>
                            ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="p-4 bg-slate-50 rounded-xl grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Work permit number</label>
                  <p className="mt-1 text-slate-800 font-medium">
                    {selectedRequest.work_permit_number}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Order number</label>
                  <p className="mt-1 text-slate-800 font-medium">
                    {selectedRequest.order_number}
                  </p>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Turbine Information</label>
                  <p className="mt-1 text-slate-800">
                    Turbine: {selectedRequest.turbine_data.turbine_number}
                    <br />
                    <span className="text-slate-600">{selectedRequest.turbine_data.type_data.name} - {selectedRequest.turbine_data.model_data.name}</span>
                  </p>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Location</label>
                  <p className="mt-1 text-slate-800">{selectedRequest.turbine_data.location}</p>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Start</label>
                  {isEditing ? (
                    <input
                      type="datetime-local"
                      value={editedStartDate}
                      onChange={(e) => setEditedStartDate(e.target.value)}
                      className="mt-1 block w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  ) : (
                    <p className="mt-1 text-slate-800">{formatDateTime(selectedRequest.planned_date_start)}</p>
                  )}
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">End</label>
                  {isEditing ? (
                    <input
                      type="datetime-local"
                      value={editedEndDate}
                      onChange={(e) => setEditedEndDate(e.target.value)}
                      className="mt-1 block w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  ) : (
                    <p className="mt-1 text-slate-800">{formatDateTime(selectedRequest.planned_date_end)}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="text-xs font-bold text-slate-500 uppercase">Work Scope</label>
                  <p className="mt-1 text-slate-800 whitespace-pre-line">{selectedRequest.work_scope_data.name}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
            {!isEditing ? (
              <>
                <button
                  onClick={() => setShowDetails(false)}
                  className="px-4 py-2 text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Close
                </button>
                {isSupervisor && selectedRequest.status === 'planned_approved' && (
                  <button
                    className="px-4 py-2 text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </button>
                )}
                {isSupervisor && selectedRequest.status === 'planned_approved' && (
                  <button
                    onClick={() =>
                      updateWorkPermitStatus(
                        selectedRequest.id,
                        selectedRequest.status
                      )
                    }
                    className="px-4 py-2 text-sm font-bold text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-colors shadow-sm"
                  >
                    Send
                  </button>
                )}
                {isSupervisor && selectedRequest.status === 'approved' && (
                  <button
                    onClick={() =>
                      updateWorkPermitStatus(
                        selectedRequest.id,
                        selectedRequest.status
                      )
                    }
                    className="px-4 py-2 text-sm font-bold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors shadow-sm"
                  >
                    Done
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 text-sm font-bold text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-colors shadow-sm"
                >
                  Save & Send
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const filteredRequests = getSortedRequests();
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const paginatedRequests = filteredRequests.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getVisiblePages = () => {
    const maxVisiblePages = 5; // Bieżąca strona + 2 do przodu + 2 do tyłu
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Header Section */}
      <div className="flex-shrink-0 p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <h1 className="text-3xl font-bold text-slate-900">Work Permits</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 mx-6 mb-6 overflow-hidden">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-slate-200 h-full flex flex-col">
        {/* Search and Filter */}
        <div className="p-4 flex-shrink-0 border-b border-slate-100">
        <div className="flex flex-col md:flex-row justify-between mb-4 gap-4">
          <div className="relative flex-1 w-full">
            <input
              placeholder="Search by order number, turbine, location..."
              className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-5 w-5" />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-slate-300 rounded-lg px-4 py-2.5 w-60 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            aria-label="Filter by status"
          >
            <option value="all">All Statuses</option>
            <option value="planned_approved">Pending</option>
            <option value="signed">Signed</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="closed">Completed</option>
          </select>
        </div>
        </div>

        {/* Table container */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="flex-1 overflow-y-auto">
            <table className="min-w-full">
              <thead className="bg-steel-100 border-b-2 border-brand-500 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider min-w-[120px]">
                    <button
                      className="flex items-center gap-1 hover:text-slate-900"
                      onClick={() => sortRequests('order_number')}
                    >
                      Order Number
                      {renderSortIndicator('order_number')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider min-w-[120px]">
                    <button
                      className="flex items-center gap-1 hover:text-slate-900"
                      onClick={() => sortRequests('work_permit_number')}
                    >
                      Work Permit Number
                      {renderSortIndicator('work_permit_number')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider min-w-[140px]">
                    <button
                      className="flex items-center gap-1 hover:text-slate-900"
                      onClick={() => sortRequests('turbine_data.turbine_number')}
                    >
                      Turbine Info
                      {renderSortIndicator('turbine_data.turbine_number')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider min-w-[160px]">
                    <button
                      className="flex items-center gap-1 hover:text-slate-900"
                      onClick={() => sortRequests('planned_date_start')}
                    >
                      Schedule
                      {renderSortIndicator('planned_date_start')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider min-w-[140px]">
                    Supervisor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider w-[60px]">
                    <button
                      className="flex items-center gap-1 hover:text-slate-900"
                      onClick={() => sortRequests('status')}
                    >
                      Status
                      {renderSortIndicator('status')}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider w-[60px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      Loading work permits...
                    </td>
                  </tr>
                ) : paginatedRequests.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      No work permits available.
                    </td>
                  </tr>
                ) : (
                  paginatedRequests.map((request) => (
                    <tr
                      key={request.id}
                      className="hover:bg-brand-50 transition-colors cursor-pointer"
                      onDoubleClick={() => handleViewDetails(request)}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-slate-900 truncate max-w-[200px]">
                        {request.order_number}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900 truncate max-w-[200px]">
                        {request.work_permit_number}
                        <div className="text-sm text-slate-400 truncate max-w-[200px]">{request.turbine_data.location}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 truncate max-w-[200px]">
                        {request.turbine_data.turbine_number}
                        <div className="text-sm text-slate-400 truncate max-w-[200px]">
                          {request.turbine_data.model_data.name} - {request.turbine_data.type_data.name}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 truncate max-w-[200px]">
                        {formatDateTime(request.planned_date_start)}<br />
                        to {formatDateTime(request.planned_date_end)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 truncate max-w-[200px]">
                        {(() => {
                          const teamLeader = request.team_data?.users?.find(
                            (user) => user.id === request.team_data.team_leader
                          );
                          return teamLeader ? (
                            <>
                              <div className="truncate max-w-[200px] font-medium text-slate-700">{teamLeader.first_name} {teamLeader.last_name}</div>
                              <div className="text-sm text-slate-400 truncate max-w-[200px]">{teamLeader.email}</div>
                            </>
                          ) : (
                            <span className="text-slate-400">No Supervisor</span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-sm w-[60px]">
                        {(() => {
                          const { color, displayName } = getStatusInfo(request.status);
                          return (
                            <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${color}`}>
                              {displayName}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-sm text-right w-[60px]">
                        <div className="flex justify-end gap-2">
                          <button
                            className="p-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-brand-100 hover:text-brand-600 transition-colors"
                            onClick={() => handleViewDetails(request)}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {['approved', 'closed'].includes(request.status) && (
                            <button
                              className="p-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-brand-100 hover:text-brand-600 transition-colors"
                              onClick={() => handleViewDocuments(request)}
                              title="View Documents"
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-4 border-t border-slate-200 bg-slate-50">
            <div className="flex items-center gap-4">
              <div className="text-sm text-slate-600">
                Showing {(currentPage - 1) * itemsPerPage + 1}-
                {Math.min(currentPage * itemsPerPage, filteredRequests.length)} of {filteredRequests.length} items
              </div>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="text-sm px-4 py-2 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 min-w-[140px]"
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              {currentPage > 1 && (
                <button
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 flex items-center justify-center text-sm font-medium transition-colors"
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-chevron-left h-4 w-4 mr-1"
                  >
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                  Previous
                </button>
              )}
              {getVisiblePages().map((page) => (
                <button
                  key={page}
                  className={`px-4 py-2 border ${
                    page === currentPage
                      ? 'border-brand-500 bg-brand-50 text-brand-700 font-bold'
                      : 'border-slate-300 text-slate-700 hover:bg-slate-100'
                  } rounded-lg flex items-center justify-center text-sm font-medium transition-colors`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              {currentPage < totalPages && (
                <button
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 flex items-center justify-center text-sm font-medium transition-colors"
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-chevron-right h-4 w-4 ml-1"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>

      <RequestDetailsModal />
    </div>
  );
};

export default RequestList;